
import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import dayjs from "dayjs";
import { Button } from "@mui/material";
import TextAreaComponents from "../../../sharedComponent/TextAreaComponents/TextAreaComponents";
import CustomDateTimePicker from "../../../sharedComponent/datetimePicker/CustomDateTimePicker";
import DataTable from "../../../sharedComponent/dataTable/DataTable";
import DateFormater from "../../../sharedComponent/DateFormater/DateFormater";
import * as XLSX from "xlsx";

function IssueReport() {
  const errorToast = (msg) => {
    toast.error(msg, {
      position: "top-right",
      autoClose: 5000,
      theme: "colored",
    });
  };

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [filters, setFilters] = useState(null);

  const [pageState, setPageState] = useState({
    isLoading: false,
    total: 0,
    page: 1,
    pageSize: 10,
  });

  const columns = [
    { field: "sn", headerName: "S/N", flex: 1 },
    { field: "salesOrderNo", headerName: "Sales Order Number", flex: 6 },
    {
      field: "orderDate",
      headerName: "Order Date",
      renderCell: (params) => <span>{DateFormater(params.row.orderDate)}</span>,
      flex: 4,
    },
    { field: "number", headerName: "Sales Bill Number", flex: 6 },
    {
      field: "billDate",
      headerName: "Bill Date",
      renderCell: (params) => <span>{DateFormater(params.row.billDate)}</span>,
      flex: 4,
    },
    { field: "issueNo", headerName: "Issue Id", flex: 6 },
    { field: "itemGroupId", headerName: "Item Group Id", flex: 3 },
    { field: "groupName", headerName: "Item Group Name", flex: 3 },
    { field: "itemId", headerName: "Item Id", flex: 2 },
    { field: "itemName", headerName: "Item Name", flex: 5 },
    { field: "qty", headerName: "Qty", flex: 1 },
    { field: "unitDescription", headerName: "Measurement Unit", flex: 4 },
    { field: "unitPrice", headerName: "Unit Price", flex: 3 },
    { field: "amount", headerName: "Amount", flex: 2 },
  ];

  const fetchReportData = async (currentPageState, filters) => {
    try {
      setPageState((old) => ({ ...old, isLoading: true }));
      setIsLoading(true);

      const url = `${process.env.REACT_APP_SECRET}/api/SalesBill/GetOrderSalesBillBySearch`;

      const body = {
        startRecordNo: currentPageState.page,
        rowPerPage: currentPageState.pageSize,
        whereClause: "1=1",
        sortColumn: "number",
        sortOrder: "desc",
        fromDate: filters?.fromDate || null,
        toDate: filters?.toDate || null,
      };

      const response = await axios.post(url, body, {
        headers: { "Content-Type": "application/json" },
      });

      const { salesOrderBillview = [], _totalCount = 0 } = response.data;

      const formattedData = salesOrderBillview.map((item, index) => ({
        ...item,
        sn: currentPageState.page * currentPageState.pageSize + index + 1,
        id: item.id || index,
      }));

      setReportData(formattedData);
      setPageState((old) => ({
        ...old,
        isLoading: false,
        total: _totalCount,
      }));
    } catch (error) {
      console.error(error);
      setPageState((old) => ({ ...old, isLoading: false }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExcelData = async () => {
    if (!fromDate || !toDate) {
      errorToast("Please select both From and To dates.");
      return;
    }
  
    try {
      setIsLoading(true);
      const from = dayjs(fromDate).format("YYYY-MM-DD");
      const to = dayjs(toDate).format("YYYY-MM-DD");
  
      const url = `${process.env.REACT_APP_SECRET}/api/SalesBill/GetAllOrderSalesBill?fromDate=${from}&toDate=${to}`;
      
      const response = await axios.post(url);
  
      if (!response.data || !response.data.salesOrderBillview) {
        throw new Error("Invalid data structure from API");
      }
  
      const data = response.data.salesOrderBillview;
  
      if (data.length === 0) {
        errorToast("No data found for the selected date range.");
        return;
      }
  
      const excelData = data.map((item, index) => ({
        "S/N": index + 1,
        "Sales Order Number": item.salesOrderNo || "",
        "Order Date": item.orderDate ? dayjs(item.orderDate).format("YYYY-MM-DD") : "",
        "Sales Bill Number": item.number || "",
        "Bill Date": item.billDate ? dayjs(item.billDate).format("YYYY-MM-DD") : "",
        "Issue Id": item.issueNo || "",
        "Item Group Id": item.itemGroupId || "",
        "Item Group Name": item.groupName || "",
        "Item Id": item.itemId || "",
        "Item Name": item.itemName || "",
        "Qty": item.qty || 0,
        "Measurement Unit": item.unitDescription || "",
        "Unit Price": item.unitPrice || 0,
        "Amount": item.amount || 0,
      }));
  
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Issue Report");
  
      const filename = `Issue_Report_${from}_to_${to}.xlsx`;
  
      XLSX.writeFile(workbook, filename);
  

      toast.success("Excel downloaded successfully!", {
        position: "top-right",
        autoClose: 4000,
        theme: "colored",
      });
  
    } catch (error) {
      errorToast("Failed to export Excel. Please try again.");
      console.error("Excel export error:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSearch = () => {
    const from = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null;
    const to = toDate ? dayjs(toDate).format("YYYY-MM-DD") : null;

    const searchFilters = { fromDate: from, toDate: to };
    setFilters(searchFilters);
    setPageState((old) => ({ ...old, page: 0 }));
    fetchReportData({ ...pageState, page: 0 }, searchFilters);
  };

  const handlePageChange = (newPage) => {
    setPageState((old) => ({ ...old, page: newPage }));
    if (filters) {
      fetchReportData({ ...pageState,page: newPage.page }, filters);
    }
  };

  // React.useEffect(() => {
  //   if (filters) {
  //     fetchReportData(pageState, filters);
  //   }
  // }, [pageState.page]);

  return (
    <div className="container-fluid">
      <ToastContainer />
      <h5>Issue Report</h5>

      <div className="card card-shadow p-3" style={{marginTop: "10px"}}>
        <div className="row">
          <div className="col-md-3">
            <CustomDateTimePicker value={setFromDate} label="From Date" val={fromDate} />
          </div>
          <div className="col-md-3">
            <CustomDateTimePicker value={setToDate} label="To Date" val={toDate} minDate={fromDate} />
          </div>
          <div className="col-md-4">
            <TextAreaComponents
              id="search"
              value={setSearchText}
              label="Search (Number/Reference)"
              placeholder="Enter search term"
              val={searchText}
            />
          </div>
          
          <div className="col-md-2 d-flex align-items-end">
            <button className="btn-yes btn-common w-100" onClick={handleSearch} disabled={isLoading}>
              {isLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end mb-2">
  <button 
    className="btn btn-success" 
    style={{ marginTop: '8px', minWidth: '160px' }} 
    onClick={fetchExcelData}
  >
    Download Excel
  </button>
</div>
      <div className="card card-shadow p-3 mt-3" style={{ overflowX: "auto" }}>



        <div style={{ minWidth: "1500px" }}>
          <DataTable
            rows={reportData}
            columns={columns}
            isLoading={pageState.isLoading}
            pageState={pageState}
            //onPageChange={handlePageChange}
            GetPurchaseOrderNoAPi={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
}

export default IssueReport;
